import { OptionTradingServiceStrategy } from './option-trading-service-strategy.service';
import { HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TransactionType, OrderType, DeskRouteTimeZone, DeskRouteCode, OrderDuration, AccountType, Account, UserType, OptionStrategy } from '../../models/common/trading-common.model';
import { TradingEntitlements } from '../../models/common/trading-entitlements';
import { TRADING_PLATFORM } from '../../utils/trading-constants';
import { Injectable } from "@angular/core";
import { isAveragePriceAccount } from '../../utils';

@Injectable()
export class ProfessionalOptionTradingServiceStrategy extends OptionTradingServiceStrategy {
    private static SUPPORTED_PLATFORM = TRADING_PLATFORM.PROFESSIONAL;

    support(platform: string): boolean {
        return ProfessionalOptionTradingServiceStrategy.SUPPORTED_PLATFORM === platform;
    }

    public accountTypes(account: Account, transactionType: TransactionType, symAccountType: AccountType): AccountType[] {
        if (!!account && !!transactionType && !!symAccountType) {
            if (transactionType === TransactionType.CLOSE_CALLS_OR_PUTS) {
                return [symAccountType];
            }
            if(transactionType === TransactionType.SELL_COVERED_CALLS) {
                return [AccountType.CASH, AccountType.SHORT];
            }
            return this.getDefaultAccountTypes(account);
        } else if (!!account) {
            return this.getDefaultAccountTypes(account);
        }
    }

    private getDefaultAccountTypes(account: Account): AccountType[] {
        const isMarginEnabledAccount: boolean = account.acctMarginInd === 'Y' || account.applicantMarginCd === '001';

        if ( isMarginEnabledAccount ) {
            return [AccountType.CASH, AccountType.MARGIN, AccountType.SHORT];
        }

        return [AccountType.CASH, AccountType.SHORT];
    }

    public getOptionStrategies(account: Account, entitlements: TradingEntitlements): string[] {
        const isMarginEnabledAccount: boolean = isAveragePriceAccount(account.acctNum) || account.marginEligibleInd === 'Y' || account.applicantMarginCd === '001';
        if (isMarginEnabledAccount) {
            return this.getMarginAccountOptionStrategies(entitlements);
        } else {
            return this.getNonMarginAccountOptionStrategies(entitlements);
        }
    }

    public getOptionMultiLegTransactionTypes(account: Account ,entitlements: TradingEntitlements, strategy: OptionStrategy, isAPA?: boolean): TransactionType[] {
        let transactionTypes: TransactionType[] = [];

        if (!entitlements.isOptionBuySellToOpenEnabled) {
            return transactionTypes;
        }
       
        if (strategy === OptionStrategy.ROLL && isAPA) {
            transactionTypes.push(TransactionType.BUY_TO_CLOSE);
            transactionTypes.push(TransactionType.SELL_TO_CLOSE);
            return transactionTypes;
        }

        if (strategy !== OptionStrategy.BUY_WRITE) {  // BUY_WRITE doesn't have transaction BUY_TO_OPEN
            transactionTypes.push(TransactionType.BUY_TO_OPEN);
        }

        if (strategy === OptionStrategy.BUY_WRITE) {
            transactionTypes.push(TransactionType.SELL_TO_OPEN);
            if (entitlements.isOptionCloseCallOrPutsEnabled) {
                transactionTypes.push(TransactionType.BUY_TO_CLOSE);
            }
            return transactionTypes;
        }

        if (entitlements.isOptionCloseCallOrPutsEnabled && strategy !== OptionStrategy.MARRIED_PUT) {
            transactionTypes.push(TransactionType.BUY_TO_CLOSE);
        }

        if (strategy !== OptionStrategy.MARRIED_PUT) {
            transactionTypes.push(TransactionType.SELL_TO_OPEN);
        }

        if (entitlements.isOptionCloseCallOrPutsEnabled) { // BUY_WRITE doesn't have transaction SELL_TO_CLOSE
            transactionTypes.push(TransactionType.SELL_TO_CLOSE);
        }

        return transactionTypes;
    }

    public getTransactionTypes(account: Account ,entitlements: TradingEntitlements, isAPA: boolean): TransactionType[] {
        let transactionTypes: TransactionType[] = [];
        if (entitlements.isOptionBuySellToOpenEnabled) {
            transactionTypes.push(TransactionType.BUY_TO_OPEN);
            transactionTypes.push(TransactionType.SELL_TO_OPEN);

            if (isAPA) {
                transactionTypes.push(TransactionType.BUY_TO_CLOSE);
                transactionTypes.push(TransactionType.SELL_TO_CLOSE);
            }
        }
        if (entitlements.isOptionCloseCallOrPutsEnabled) {
            transactionTypes.push(TransactionType.CLOSE_CALLS_OR_PUTS);
        }
        if (entitlements.isOptionSellCoveredCallsEnabled) {
            transactionTypes.push(TransactionType.SELL_COVERED_CALLS);
        }

        return transactionTypes;
    }

    public getOrderTypes(isNonDollar: boolean, qty: number): OrderType[] {
        let orderTypes: OrderType[] = [];

        if (qty >= 1000) {
          orderTypes.push(OrderType.MARKET);
          orderTypes.push(OrderType.LIMIT);
        } else {
          orderTypes.push(OrderType.MARKET);
          orderTypes.push(OrderType.LIMIT);
          orderTypes.push(OrderType.STOP_LIMIT);
          orderTypes.push(OrderType.STOP);
        }
        return orderTypes;
    }

    public pushStraddleStrangleOrderTypes(strategy: OptionStrategy, transactionType: TransactionType, orderTypes:OrderType[]) {
        if ( TransactionType.BUY_TO_OPEN === transactionType || TransactionType.BUY_TO_CLOSE === transactionType ) {
            orderTypes.push(OrderType.NET_DEBIT);
        } else if ( TransactionType.SELL_TO_OPEN === transactionType || TransactionType.SELL_TO_CLOSE === transactionType ) {
            orderTypes.push(OrderType.NET_CREDIT);
        } else {
            console.log('Unexpected transaction type for ' + strategy + ' order: ' + transactionType +
                '. Unable to determine valid Order Types.');
        }
    }
    public pushBuyWriteOrderTypes(transactionType: TransactionType, orderTypes:OrderType[]) {
        if ( TransactionType.SELL_TO_OPEN === transactionType ) {
            orderTypes.push(OrderType.NET_DEBIT);
        } else if ( TransactionType.BUY_TO_CLOSE === transactionType ) {
            orderTypes.push(OrderType.NET_CREDIT);
        } else {
            console.log('Unexpected transaction type for Buy-Write order: ' + transactionType +
                '. Unable to determine valid Order Types.');
        }
    }

    public pushMarriedPutOrderTypes(transactionType:TransactionType, orderTypes:OrderType[]){
        if ( TransactionType.BUY_TO_OPEN === transactionType ) {
            orderTypes.push(OrderType.NET_DEBIT);
        } else if ( TransactionType.SELL_TO_CLOSE === transactionType ) {
            orderTypes.push(OrderType.NET_CREDIT);
        } else {
            console.log('Unexpected transaction type for Married Put order: ' + transactionType +
                '. Unable to determine valid Order Types.');
        }
    }

    public getOptionMultiLegOrderTypes(strategy: OptionStrategy, transactionType: TransactionType, modifier: any = {}): OrderType[] {
        console.info('Getting order types for multi-leg option strategy ' + strategy +
            ' using transaction type ' + transactionType);

        if ( !transactionType ) { // without transactionType
            return [OrderType.MARKET, OrderType.NET_CREDIT, OrderType.NET_DEBIT, OrderType.EVEN];
        }

        // Any strategy except "Single" will use this function to derive the proper Order Type options
        let orderTypes: OrderType[] = [];

        // All strategies can use Market
        orderTypes.push(OrderType.MARKET);

        switch ( strategy ) {
            case OptionStrategy.CALENDAR_SPREAD:
            case OptionStrategy.DIAGONAL_SPREAD:
            case OptionStrategy.VERTICAL_SPREAD:
            case OptionStrategy.COLLAR:
            case OptionStrategy.ROLL:
                // These strategies are always a combo of a Buy and a Sell, so they can be any order type.
                orderTypes.push(OrderType.NET_CREDIT);
                orderTypes.push(OrderType.NET_DEBIT);
                orderTypes.push(OrderType.EVEN);
                break;
            case OptionStrategy.STRADDLE:
            case OptionStrategy.STRANGLE:
                // These strategies are always two Buys or two Sells, so they can never be Order Type "Even".
                // Buy-side orders can only add Net Debit to the list of options (client is paying a Premium)
                // Sell-side orders can only add Net Credit (client is getting the Premium)

                this.pushStraddleStrangleOrderTypes(strategy, transactionType, orderTypes);
                break;
            case OptionStrategy.BUY_WRITE:
                // Buy-Write orders can only be Sell to Open or Buy to Close on the option leg.
                //
                // Because a Buy-Write involves buying an equity while selling a Call (or vice-versa), the equity's
                // portion of the principal always dwarfs that of the option leg (typically 100 equity shares per
                // Call contract, where the equity share price is much greater than the Call's). As a result, the
                // determination of Net Debit or Net Credit as an allowed Order Type option is reversed from the
                // logic used for Straddle and Strangle. And because of the large difference in Principal between
                // the option and equity legs, the order can never be Even.

                this.pushBuyWriteOrderTypes(transactionType, orderTypes);
                break;
            case OptionStrategy.MARRIED_PUT:
                // Married Put orders can only be Buy to Open or Sell to Close on the option leg.
                //
                // Because a Married Put involves buying an equity while buying a Put (or vice-versa), the equity's
                // portion of the principal always dwarfs that of the option leg (typically 100 equity shares per
                // Put contract, where the equity share price is much greater than the Put's). As a result, the
                // determination of Net Debit or Net Credit as an allowed Order Type option is similar to the
                // logic used for Straddle and Strangle. And because of the large difference in Principal between
                // the option and equity legs, the order can never be Even.

                this.pushMarriedPutOrderTypes(transactionType, orderTypes);
                break;
            default:
        }

        // Additional modify -
        // GLOBOEM-5953 Restrict selection of Net Credit / Net Debit based on Net Bid and Net Ask
        let idx = orderTypes.indexOf(OrderType.NET_CREDIT);
        if ( !!modifier.dontShowNetCredit && idx > 0 ) {
            orderTypes.splice(idx, 1);
        }

        idx = orderTypes.indexOf(OrderType.NET_DEBIT);
        if ( !!modifier.dontShowNetDebit && idx > 0 ) {
            orderTypes.splice(idx, 1);
        }

        return orderTypes;
    }

    public getDurations(isMultiLegOrder: boolean, orderType?: string, ordQty?: number, userType?: UserType): OrderDuration[] {
        if (isMultiLegOrder || ordQty >= 1000) {
          return [OrderDuration.DAY];
        }

        if (orderType) {
            switch (orderType) {
                case OrderType.MARKET: {
                   return [OrderDuration.DAY];
                }
                case OrderType.LIMIT: {
                    return this.getDurationsByUserType(userType, ordQty);
                }
                case OrderType.STOP_LIMIT: {
                    return [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.GTM];
                 }
                 case OrderType.STOP: {
                    return [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.GTM];
                 }

                default: {
                    return this.getDurationsByUserType(userType);
                }
             }
        } else {
            return this.getDurationsByUserType(userType);
        }
    }

    private getDurationsByUserType(userType?: UserType, ordQty?: number): OrderDuration[] {
        let orderDurations: OrderDuration[] = [];
        if (userType === UserType.BD) {
            orderDurations = [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.GTM];
        } else if (userType === UserType.RIA) {
            orderDurations =  [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.GTM, OrderDuration.IOC];
        } else {
            orderDurations =  [OrderDuration.DAY, OrderDuration.GTC, OrderDuration.GTD, OrderDuration.GTM, OrderDuration.IOC];
        }
        if(ordQty>1){
            orderDurations.push(OrderDuration.FOK);
        }
        return orderDurations;
    }

    public getDeskRouteTimeZone(): DeskRouteTimeZone[] {
        return [DeskRouteTimeZone.CT, DeskRouteTimeZone.PT, DeskRouteTimeZone.MT, DeskRouteTimeZone.ET];
    }

    public getDeskCode(): DeskRouteCode[] {
        return [DeskRouteCode.A, DeskRouteCode.AR, DeskRouteCode.B, DeskRouteCode.C,
             DeskRouteCode.D, DeskRouteCode.IS, DeskRouteCode.IN, DeskRouteCode.O, DeskRouteCode.PF,
             DeskRouteCode.PT, DeskRouteCode.PR, DeskRouteCode.S,
             DeskRouteCode.TR, DeskRouteCode.SW, DeskRouteCode.EC, DeskRouteCode.CR];
    }

    public getCodeValuesExchangeList(): Observable<any> {
        let codeValuesExchangeListUrl: string = this.getBaseUrl() + '/code-values-exchange-list';
        return this.http.get(codeValuesExchangeListUrl, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            })
        });
    }
}
